<template>
  <registration-layout
    :steps="steps"
    :step-id="currentStepID"
    :disable-step-clicks="true"
    size="tiny"
    class="account-registration-add-members"
  >
    <h3>Invite your teammates</h3>
    <div id="email" style="display: none;">{{ email }}</div>
    <el-form class="account-registration-add-members__form">
      <div v-for="(inviteeEmail, index) in listEmailAddresses.length" :key="index">
        <el-form-item>
          <standard-email-input 
            v-model="listEmailAddresses[index]"
            placeholder="Invite via email..."
            @invalid-on-blur="addInvalidEmailIndex(index)"
            @valid-on-blur="addValidEmailIndex(index)"
          />
        </el-form-item>
      </div>
      <dd-button
        v-if="enabledNewInvites"
        class="add-another"
        type="text"
        @click="addRow()"
      >+ Add another teammate</dd-button>
      <dd-button :is-loading="isSubmitting" :is-disabled="disableSubmit" color="blue" @click="submitForm">Add teammates</dd-button>
      <p>
        <router-link 
          :to="RouteAssistant.getDashboardRoute($route.params.accountId)"
          class="just-a-link">
          Not right now. Just take me to my dashboard!
        </router-link>
      </p>
    </el-form>
  </registration-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import RegistrationLayout from '@/layouts/registration-layout.vue'
import MainContainer from '@/layouts/main-container.vue'

/* Mixins */

/* Components */
import StandardEmailInput from '@/components/global/inputs/standard-email-input.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { IItemForSelection } from '@/api/types/response.types'

/* API */
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default defineComponent({
  name: 'AccountRegistrationAddMembers',

  components: {
    RegistrationLayout,
    MainContainer,
  },

  data() {
    return {
      RouteAssistant,
      isSubmitting: false,
      invalidEmailRows: [] as number[],
      StandardEmailInput,
      listEmailAddresses: ['', '', ''],
      steps: [
        { id: 1, name: 'You' },
        { id: 2, name: 'Company' },
        { id: 3, name: 'Team' },
      ] as IItemForSelection[],
      currentStepID: 3,
    }
  },

  head(): any {
    return {
      title: 'Invite team members',
    }
  },

  computed: {
    enabledNewInvites(): boolean {
      return this.listEmailAddresses.length < 10
    },
    disableSubmit(): boolean {
      return this.invalidEmailRows.length > 0
    },
    email(): string {
      return this.$route.query.email as string
    },
  },

  methods: {
    addRow(): void {
      /* Stub new row with blank email but carryover other columns */
      if (this.enabledNewInvites) {
        this.listEmailAddresses.push('')
      }
    },
    addInvalidEmailIndex(index: number) {
      // Add the index from the invalid list...
      if (this.invalidEmailRows.includes(index)) {
        return
      }
      this.invalidEmailRows.push(index)
    },
    addValidEmailIndex(index: number) {
      // Remove the index from the invalid list...
      if (this.invalidEmailRows.includes(index)) {
        this.invalidEmailRows = this.invalidEmailRows.filter(e => e !== index)
      }
    },
    async submitForm() {
      const noEmails =
        this.listEmailAddresses.filter(e => e !== '').length === 0

      if (noEmails) {
        RouteAssistant.goToDashboard(this.$router, this.$route.params.accountId)
        return
      }

      this.isSubmitting = true
      try {
        const response = await AccountMembershipsAPI.post_InviteCoworkers(
          this.listEmailAddresses,
        )
        /* If no successful emails, response does not emit a message. Just go to dashboard. */
        const successMessage = this.composeSuccessMessage(
          response.message,
          response.upgradedToProfessional,
          response.seatsIncreased,
          response.newTotalSeats,
        )
        if (successMessage) {
          this.$messaging.showSuccess(successMessage)
        }
        RouteAssistant.goToDashboard(this.$router, this.$route.params.accountId)
      } catch (error) {
        this.$error.emit(error, true)
      }

      this.isSubmitting = false
    },
    composeSuccessMessage(
      inviteMessage: string,
      upgradedToProfessional: boolean,
      seatsIncreased: boolean,
      newTotalSeats: number | null,
    ): string {
      // Append a trial-mode auto-upgrade notice when the API bumped the plan/seats to fit
      // the invite list. We surface this so users aren't surprised by the change later.
      // newTotalSeats (paid + free) is what users intuit as their "seat count" -- newSeatCount
      // on the same response is paid-only and would under-report when FreeSeats > 0.
      if (upgradedToProfessional && newTotalSeats) {
        const upgradeNotice = `We moved you to the Professional plan with ${newTotalSeats} seats to fit your team — no charge during your trial.`
        return inviteMessage
          ? `${inviteMessage} ${upgradeNotice}`
          : upgradeNotice
      }
      if (seatsIncreased && newTotalSeats) {
        const seatNotice = `We bumped your seat count to ${newTotalSeats} to fit your team — no charge during your trial.`
        return inviteMessage ? `${inviteMessage} ${seatNotice}` : seatNotice
      }
      return inviteMessage
    },
  },
})
</script>

<style lang="scss">
.account-registration-add-members {
  &__form {
    .add-another {
      margin-bottom: 10px;
    }

    > div:last-of-type .el-form-item {
      margin-bottom: 10px;
    }

    p {
      padding-top: 40px;
      text-align: center;
      @include lettering--p-sm;
    }
  }
}
</style>
